import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Slider,
  FormControlLabel,
  Checkbox,
  Button
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import PriceDisplay from "components/PriceDisplay";
import iaasService from "services/iaasService";
import Swal from "sweetalert2";
import { AuthContext } from "context";

function PrivateCloudConfig({ onBack }) {
  const { user } = useContext(AuthContext);
  const [ram, setRam] = useState(32);
  const [storage, setStorage] = useState(1000);
  const [cores, setCores] = useState(4);
  const [dedicatedHardware, setDedicatedHardware] = useState(false);
  const [haEnabled, setHaEnabled] = useState(false);
  const [backupEnabled, setBackupEnabled] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);

  const showSuccessNotification = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Erfolg!',
      text: message,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  const showErrorNotification = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Fehler',
      text: message,
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  };

  useEffect(() => {
    const config = {
      ram,
      storage,
      cores,
      dedicatedHardware,
      haEnabled,
      backupEnabled
    };
    
    // Berechne den Basispreis
    const basePrice = iaasService.calculatePrivateCloudPrice(config);
    
    const isPartner = user?.isPartner === true;

    if (isPartner) {
      // 20% Rabatt für Partner
      setOriginalPrice(basePrice);
      setTotalPrice(basePrice * 0.8);
      setDiscountApplied(true);
    } else {
      setTotalPrice(basePrice);
      setOriginalPrice(null);
      setDiscountApplied(false);
    }
  }, [ram, storage, cores, dedicatedHardware, haEnabled, backupEnabled, user?.isPartner]);

  const handleOrder = async () => {
    if (!agreedToTerms) {
      showErrorNotification("Bitte stimmen Sie den AGBs zu.");
      return;
    }

    try {
      const configuration = {
        ram,
        storage,
        cores,
        dedicatedHardware,
        haEnabled,
        backupEnabled
      };
      
      // Füge Rabattinformationen hinzu, wenn ein Rabatt angewendet wurde
      if (discountApplied) {
        configuration.priceInfo = {
          originalPrice: originalPrice,
          discountApplied: true
        };
      }

      const response = await iaasService.orderService(
        'private-cloud',
        configuration
      );

      if (response) {
        showSuccessNotification("Ihre Bestellung wurde erfolgreich aufgegeben!");
        setTimeout(() => {
          window.location.href = "/iaas/server";
        }, 2000);
      }
    } catch (error) {
      console.error("Fehler bei der Bestellung:", error);
      showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
    }
  };

  return (
    <SoftBox>
      <SoftBox p={3} border="1px solid #e9ecef" borderRadius="lg" bgcolor="#f8f9fa">
        <SoftTypography variant="h4" fontWeight="medium" color="dark" mb={3}>
          UDS Private Cloud Konfiguration
        </SoftTypography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
              <SoftTypography variant="h6" mb={2}>RAM-Pool</SoftTypography>
              <Slider
                value={ram}
                min={32}
                max={220}
                step={32}
                onChange={(_, value) => setRam(value)}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-track': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-rail': {
                    color: '#cccccc',
                  }
                }}
                marks={[
                  { value: 32, label: '32 GB' },
                  { value: 128, label: '128 GB' },
                  { value: 256, label: '256 GB' },
                  { value: 512, label: '512 GB' }
                ]}
              />
              <SoftTypography variant="body2" color="text" textAlign="right" mt={1}>
                RAM-Pool: {ram} GB
              </SoftTypography>
            </SoftBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
              <SoftTypography variant="h6" mb={2}>Storage-Pool</SoftTypography>
              <Slider
                value={storage}
                min={500}
                max={5000}
                step={500}
                onChange={(_, value) => setStorage(value)}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-track': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-rail': {
                    color: '#cccccc',
                  }
                }}
                marks={[
                  { value: 500, label: '500 GB' },
                  { value: 2000, label: '2 TB' },
                  { value: 5000, label: '5 TB' }
                ]}
              />
              <SoftTypography variant="body2" color="text" textAlign="right" mt={1}>
                Storage-Pool: {storage} GB
              </SoftTypography>
            </SoftBox>
          </Grid>

          <Grid item xs={12}>
            <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
              <SoftTypography variant="h6" mb={2}>CPU-Kerne</SoftTypography>
              <Slider
                value={cores}
                min={4}
                max={12}
                step={2}
                onChange={(_, value) => setCores(value)}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-track': {
                    color: '#8b0000',
                  },
                  '& .MuiSlider-rail': {
                    color: '#cccccc',
                  }
                }}
                marks={[
                  { value: 4, label: '4 Kerne' },
                  { value: 8, label: '8 Kerne' },
                  { value: 10, label: '10 Kerne' },
                  { value: 12, label: '12 Kerne' }
                ]}
              />
              <SoftTypography variant="body2" color="text" textAlign="right" mt={1}>
                CPU-Kerne: {cores}
              </SoftTypography>
            </SoftBox>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dedicatedHardware}
                  onChange={(e) => setDedicatedHardware(e.target.checked)}
                  sx={{
                    color: '#8b0000',
                    '&.Mui-checked': {
                      color: '#8b0000',
                    }
                  }}
                />
              }
              label="Dedizierte Hardware"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={haEnabled}
                  onChange={(e) => setHaEnabled(e.target.checked)}
                  sx={{
                    color: '#8b0000',
                    '&.Mui-checked': {
                      color: '#8b0000',
                    }
                  }}
                />
              }
              label="High Availability (HA) Setup"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={backupEnabled}
                  onChange={(e) => setBackupEnabled(e.target.checked)}
                  sx={{
                    color: '#8b0000',
                    '&.Mui-checked': {
                      color: '#8b0000',
                    }
                  }}
                />
              }
              label="Tägliches Backup aktivieren"
            />
          </Grid>

          <Grid item xs={12}>
            <SoftBox p={3} border="1px solid #e9ecef" borderRadius="lg" bgcolor="#f8f9fa">
              <PriceDisplay 
                price={totalPrice} 
                originalPrice={originalPrice} 
                discountApplied={discountApplied} 
              />
            </SoftBox>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  sx={{
                    color: '#8b0000',
                    '&.Mui-checked': {
                      color: '#8b0000',
                    }
                  }}
                />
              }
              label={
                <SoftTypography variant="body2">
                  Ich akzeptiere die{" "}
                  <a href="/agb" target="_blank" rel="noopener noreferrer" style={{ color: '#8b0000' }}>
                    AGBs
                  </a>{" "}
                  und bestelle die Private Cloud kostenpflichtig.
                </SoftTypography>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftButton 
                variant="outlined"
                onClick={onBack}
                sx={{
                  borderColor: '#8b0000',
                  color: '#8b0000',
                  '&:hover': {
                    borderColor: '#660000',
                    backgroundColor: 'rgba(139,0,0,0.04)'
                  }
                }}
              >
                Zurück
              </SoftButton>
              <SoftButton
                variant="contained"
                onClick={handleOrder}
                disabled={!agreedToTerms}
                sx={{
                  backgroundColor: '#8b0000',
                  '&:hover': {
                    backgroundColor: '#660000'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc'
                  }
                }}
              >
                Jetzt bestellen
              </SoftButton>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </SoftBox>
  );
}

PrivateCloudConfig.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default PrivateCloudConfig;